import { isNil, get, upperCase, isFunction, lowerCase } from 'lodash';
import { isSSR } from 'src/helpers/ssr';

export type SocialNetworkNames = keyof typeof window.LTSocialAuthConfig;

// eslint-disable-next-line prettier/prettier
type SocialNetworkSdkScriptId = `${SocialNetworkNames}-sdk`;

const getSocialNetworkSdkScriptId = (socialNetwork: SocialNetworkNames) =>
  `${socialNetwork}-sdk` as SocialNetworkSdkScriptId;

const isInitSocialNetworkHandler = (id: SocialNetworkSdkScriptId) =>
  !isNil(document.getElementById(id));

const getAuthInit = (name: SocialNetworkNames) =>
  get(window, [upperCase(name), 'init']);

export function initSocialNetworkHandlers(
  credentials: typeof window.LTSocialAuthConfig,
) {
  (Object.keys(credentials) as SocialNetworkNames[]).forEach(
    (socialNetwork: SocialNetworkNames) => {
      const config = credentials[socialNetwork];

      if (!config.scriptURL) {
        return;
      }

      if (
        isInitSocialNetworkHandler(getSocialNetworkSdkScriptId(socialNetwork))
      ) {
        return;
      }

      const script = document.createElement('script');

      const loadHandler = (name, socialConfig) => (): void => {
        try {
          const initFunction = getAuthInit(name);
          if (!isFunction(initFunction)) {
            throw new Error('initFunction is not a function');
          }
          initFunction(socialConfig.credentials);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('SocialAuth: failed to init provider', name);
        }
      };

      const attrs = {
        src: config.scriptURL,
        type: 'text/javascript',
        async: true,
        onload: loadHandler(socialNetwork, config),
        id: getSocialNetworkSdkScriptId(socialNetwork),
      };

      Object.keys(attrs).forEach((key) => {
        script[key] = attrs[key];
      });

      document.body.appendChild(script);
    },
  );
}

export const getLTSocialAuthConfig = () =>
  isSSR ? undefined : window.LTSocialAuthConfig;

export type VKAuthConfig = {
  auth_url: string;
  callbackURL: string;
  credentials: {
    apiId: string;
    onlyWidgets: boolean;
    status: boolean;
    api_settings: number;
    scope: number;
  };
  id: string;
  scope: number;
  scriptURL: string;
  script_source: string;
  secret: string;
};

export type OKAuthConfig = VKAuthConfig;

export type YandexAuthConfig = VKAuthConfig;

export type TBankAuthConfig = {
  client_id: string;
  client_secret: string;
  redirectURI: string;
  auth_token?: string;
};

export type GithubAuthConfig = {
  client_id: string;
  client_secret: string;
  redirectURI: string;
  auth_url: string;
  scope: string;
};

export type AppleAuthConfig = {
  team_id: string;
  key_id: string;
  callbackURL: string;
  scriptURL: string;
  windowName: string;
  credentials: {
    clientId: string;
    redirectURI: string;
    scope: string;
    usePopup: boolean;
  };
};

export type AlfaAuthConfig = {
  client_id: string;
  client_secret: string;
  redirectURI: string;
  auth_token?: string;
};

export const getSocialAuthConfigByName = (
  name: SocialNetworkNames,
):
  | VKAuthConfig
  | TBankAuthConfig
  | AlfaAuthConfig
  | GithubAuthConfig
  | AppleAuthConfig
  | undefined => {
  const config = getLTSocialAuthConfig();

  if (!config) {
    return undefined;
  }

  // get с tbank_link работает криво и отдает undefined
  if (name === 'tbank_link') {
    return config.tbank_link;
  }

  return get(config, [lowerCase(name)]);
};
